import { initializeApp } from 'firebase/app';
import { useDispatch } from 'react-redux';
import { child, get, getDatabase, onDisconnect, ref, set, update } from 'firebase/database';
import React, { FC, Suspense, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import './App.less';
import './styles/primelex-extra.less';
import NotFound from './components/404/404.component';
import NotAuthorized from './components/not-authorized/not-authorized.component';
import { FIREBASE_CONFIG } from './config/constants';
import { useAuth } from './hooks/useAuth';
import { authRoutesToCheck } from './modules/auth';
import { SignupSuccess } from './modules/auth/signup-success/signup-success.component';
import DesignerModule, { designerRoutesToCheck } from './modules/designer';
import GDPModule, { gdpRoutesToCheck } from './modules/gdp';
import ManufacturerModule, { manufacturerRoutesToCheck } from './modules/manufacturer';
import PatientTreatmentCheckIn from './modules/patients/check-ins/check-ins';
import { CheckinSuccess } from './modules/patients/check-ins/success/success.component';
import SpecialistModule, { specialistRoutesToCheck } from './modules/specialist';
import { ViewProposal } from './modules/patients/view-proposal/view-proposal.component';
import { setGooglePlacesLoaded } from './redux/appSlice';
import { RedirectToSignInWithBackUrl } from './components/PrivateRoute';
import { EmailSuccessfullyVerifiedPage } from './modules/auth/email-successfully-verified/email-successfully-verified.component';
import { IUser } from './modules/auth/services/type';
import * as Sentry from '@sentry/react';

const AuthModule = React.lazy(() => import('./modules/auth'));

// initialise firebase app
initializeApp(FIREBASE_CONFIG);

const Loading = () => <div>Loading...</div>;

const SafeRedirect: FC<{ user: IUser | null }> = ({ user }) => {
  if (user) {
    return <Redirect to={`/${user.type}`} />;
  }
  return <RedirectToSignInWithBackUrl />;
};

const App = () => {
  const dispatch = useDispatch();
  (window as any).initMap = () => {
    dispatch(setGooglePlacesLoaded({ loading: true }));
  };

  const { user } = useAuth();
  const database = getDatabase();

  useEffect(() => {
    if (user?.id) {
      Sentry.setContext('User Info', user);
    }
  }, [user?.id]);

  // Firebase users update
  useEffect(() => {
    if (user && user?.id) {
      // database ref
      const dbRef = ref(getDatabase());

      // get user object
      get(child(dbRef, `users/${user?.id}`))
        .then((snapshot) => {
          const userRef = ref(database, `users/${user?.id}`);

          // check if exists
          if (snapshot.exists()) {
            update(userRef, {
              isOnline: true,
            })
              .then(() => {
                onDisconnect(userRef).update({ isOnline: false });
              })
              .catch((err: any) => {
                console.log('failed to update a data', err);
              });
          } else {
            set(userRef, {
              firstName: user.firstName,
              lastName: user.lastName,
              profilePic: user.profilePic ?? '',
              isOnline: true,
              type: user.type,
            })
              .then(() => {
                onDisconnect(userRef).update({ isOnline: false });
              })
              .catch((err: any) => {
                console.log('failed to create a data', err);
              });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [user]);

  // TODO: hanlde mutiple device connection

  return (
    <Switch>
      <Route exact path="/" component={SafeRedirect} />

      <Route exact path="/signup-success">
        <Suspense fallback={Loading}>
          <SignupSuccess />
        </Suspense>
      </Route>

      <Route exact path="/verify">
        <Suspense fallback={Loading}>
          <EmailSuccessfullyVerifiedPage />
        </Suspense>
      </Route>

      <Route exact path={authRoutesToCheck}>
        <Suspense fallback={Loading}>
          <AuthModule />
        </Suspense>
      </Route>

      <Route exact path={gdpRoutesToCheck}>
        <GDPModule />
      </Route>

      <Route exact path="/patients/proposal/:proposalId">
        <ViewProposal />
      </Route>

      <Route exact path={designerRoutesToCheck}>
        <DesignerModule />
      </Route>

      <Route exact path={manufacturerRoutesToCheck}>
        <ManufacturerModule />
      </Route>

      <Route exact path={specialistRoutesToCheck}>
        <SpecialistModule />
      </Route>

      <Route exact path="/patients/check-in">
        <PatientTreatmentCheckIn />
      </Route>

      <Route exact path="/patients/check-in/success">
        <CheckinSuccess />
      </Route>

      <Route path="/not-authorized" component={NotAuthorized} />

      <Route path="*" component={NotFound} />
    </Switch>
  );
};

export default App;
